import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Stock, Signal, mockStocks, mockSignals } from '../data/mockData';
import { fetchEncryptedData } from '../../utils/crypto';
import { processRawData } from '../../utils/dataAdapter';
import { DATA_URL } from '../../config';

interface DataContextType {
  stocks: Stock[];
  signals: Signal[];
  isLoading: boolean;
  error: Error | null;
  refreshData: () => Promise<void>;
  lastUpdated: Date | null;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export function DataProvider({ children }: { children: ReactNode }) {
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [signals, setSignals] = useState<Signal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('Fetching data from:', DATA_URL);
      const data = await fetchEncryptedData(DATA_URL);
      
      if (data && data.stocks) {
        console.log('Data fetched successfully, processing...');
        const { stocks: processedStocks, signals: processedSignals } = processRawData(data);
        setStocks(processedStocks);
        setSignals(processedSignals);
        setLastUpdated(new Date());
      } else {
        console.warn('Failed to load real data or data format incorrect, falling back to mock data');
        setStocks(mockStocks);
        setSignals(mockSignals);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      // Fallback to mock data on error
      setStocks(mockStocks);
      setSignals(mockSignals);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const refreshData = async () => {
    await loadData();
  };

  return (
    <DataContext.Provider value={{ stocks, signals, isLoading, error, refreshData, lastUpdated }}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}
